import React, { useEffect, useRef } from 'react';
import './Features.css';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const Features = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const featureRefs = useRef([]);
  const numberRefs = useRef([]);
  const blueprintRef = useRef(null);
  const lineRefs = useRef([]);

  const features = [
    {
      id: 1,
      number: '01',
      title: 'PERFORMANCE',
      description: 'Electric hub motors deliver smooth, consistent power designed for off road mobility and controlled handling.',
      position: 'left'
    },
    {
      id: 2,
      number: '02',
      title: 'TECHNOLOGY',
      description: 'Integrated battery management and electric systems focus on efficiency, reliability, and ease of use.',
      position: 'right'
    },
    {
      id: 3,
      number: '03',
      title: 'DESIGN',
      description: 'Compact four wheel layout provides balance and stability across varied surfaces.',
      position: 'left'
    },
    {
      id: 4,
      number: '04',
      title: 'SAFETY',
      description: 'Low center of gravity and wide stance support confident riding on uneven terrain.',
      position: 'right'
    }
  ];

  useEffect(() => {
    // Initial animations
    const ctx = gsap.context(() => {
      // Section entrance
      gsap.fromTo(sectionRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1.2,
          ease: "power2.out"
        }
      );

      // Blueprint animation
      gsap.fromTo(blueprintRef.current,
        {
          opacity: 0,
          scale: 1.1,
          rotation: -5
        },
        {
          opacity: 0.03,
          scale: 1,
          rotation: 0,
          duration: 2,
          ease: "power2.out",
          delay: 0.3
        }
      );

      // Title animation
      gsap.fromTo(titleRef.current,
        {
          opacity: 0,
          y: 40
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          delay: 0.5
        }
      );

      // Number animations with staggered delay
      numberRefs.current.forEach((number, index) => {
        if (number) {
          gsap.fromTo(number,
            {
              opacity: 0,
              scale: 0.8,
              y: 20
            },
            {
              opacity: 1,
              scale: 1,
              y: 0,
              duration: 0.8,
              ease: "back.out(1.4)",
              delay: 0.7 + (index * 0.1)
            }
          );
        }
      });

      // Feature block animations
      featureRefs.current.forEach((feature, index) => {
        if (feature) {
          gsap.fromTo(feature,
            {
              opacity: 0,
              y: 30
            },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.9 + (index * 0.15),
              scrollTrigger: {
                trigger: feature,
                start: "top 85%",
                toggleActions: "play none none reverse"
              }
            }
          );
        }
      });

      // Connection line animations
      lineRefs.current.forEach((line, index) => {
        if (line) {
          gsap.fromTo(line,
            {
              scaleX: 0,
              opacity: 0
            },
            {
              scaleX: 1,
              opacity: 0.1,
              duration: 1,
              ease: "power2.inOut",
              delay: 1.2 + (index * 0.2),
              scrollTrigger: {
                trigger: line,
                start: "top 80%",
                toggleActions: "play none none reverse"
              }
            }
          );
        }
      });

      // Hover animations setup
      featureRefs.current.forEach((feature, index) => {
        if (feature) {
          const number = numberRefs.current[index];
          
          feature.addEventListener('mouseenter', () => {
            gsap.to(number, {
              scale: 1.1,
              textShadow: '0 0 30px rgba(189, 239, 68, 0.5)',
              duration: 0.3,
              ease: "power2.out"
            });
            
            gsap.to(feature, {
              y: -5,
              duration: 0.3,
              ease: "power2.out"
            });
          });

          feature.addEventListener('mouseleave', () => {
            gsap.to(number, {
              scale: 1,
              textShadow: '0 0 20px rgba(189, 239, 68, 0.3)',
              duration: 0.3,
              ease: "power2.out"
            });
            
            gsap.to(feature, {
              y: 0,
              duration: 0.3,
              ease: "power2.out"
            });
          });
        }
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <section className="features-section" ref={sectionRef}>
      {/* Blueprint Background */}
      <div className="blueprint-bg" ref={blueprintRef}>
        <img 
          src="./bike1.png" 
          alt="EZ Raider Blueprint" 
          className="blueprint-image"
        />
      </div>

      {/* Animated Connection Lines */}
      <div className="connection-lines">
        <div className="line line-1" ref={el => lineRefs.current[0] = el}></div>
        <div className="line line-2" ref={el => lineRefs.current[1] = el}></div>
        <div className="line line-3" ref={el => lineRefs.current[2] = el}></div>
      </div>

      {/* Section Header */}
      <div className="section-header" ref={titleRef}>
        <div className="section-label">
          <div className="label-decoration"></div>
          <span className="label-text">KEY FEATURES</span>
          <div className="label-decoration"></div>
        </div>
        <h2 className="section-title">// YOUR ADVENTURE AWAITS</h2>
      </div>

      {/* Features Grid */}
      <div className="features-grid">
        {features.map((feature, index) => (
          <div 
            key={feature.id}
            ref={el => featureRefs.current[index] = el}
            className={`feature-block feature-${feature.id} ${feature.position}`}
          >
            {/* Feature Number */}
            <div 
              ref={el => numberRefs.current[index] = el}
              className="feature-number-wrapper"
            >
              <div className="feature-number-glow"></div>
              <div className="feature-number">{feature.number}</div>
            </div>

            {/* Feature Content */}
            <div className="feature-content">
              <h3 className="feature-title">
                <span className="title-slash">//</span>
                {feature.title}
              </h3>
              <div className="feature-description-wrapper">
                <div className="description-line"></div>
                <p className="feature-description">{feature.description}</p>
              </div>
            </div>

            {/* Feature Indicator */}
            <div className="feature-indicator">
              <div className="indicator-dot"></div>
              <div className="indicator-line"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Grid Lines */}
      <div className="grid-lines">
        <div className="vertical-line vl-1"></div>
        <div className="vertical-line vl-2"></div>
        <div className="horizontal-line hl-1"></div>
        <div className="horizontal-line hl-2"></div>
      </div>
    </section>
  );
};

export default Features;